import type { AtmosphereQuality } from "./atmosphere.js";

export const WATER_VISUAL_CONFIG = Object.freeze({
  maximumRipples: 16,
  maximumSplashes: 4,
  dropletsPerSplash: 12,
  maximumActors: 32,
  nearbyRadius: 48,
  rippleLifetime: 2.7,
  splashLifetime: 0.85,
  quality: Object.freeze({
    low: Object.freeze({ waves: 2, ripples: 6, localSpecular: false }),
    medium: Object.freeze({ waves: 3, ripples: 12, localSpecular: true }),
    high: Object.freeze({ waves: 4, ripples: 16, localSpecular: true }),
  }),
  // amplitude, direction X/Z, wave number, angular velocity, phase.
  waves: Object.freeze([
    Object.freeze([0.055, 0.6, 0.8, 0.80554, 1.08, 0] as const),
    Object.freeze([0.036, -0.8, 0.6, 1.309, 1.42, 1.7] as const),
    Object.freeze([0.022, 0.28, 0.96, 2.16662, 1.95, 3.1] as const),
    Object.freeze([0.012, -0.96, 0.28, 3.69599, 2.4, 0.8] as const),
  ]),
});

/** CPU reference for seam/derivative tests. Animation itself only runs on GPU. */
export function sampleVisualWaterWave(x: number, z: number, time: number, quality: AtmosphereQuality) {
  let height = 0;
  let dx = 0;
  let dz = 0;
  for (let index = 0; index < WATER_VISUAL_CONFIG.quality[quality].waves; index += 1) {
    const [amplitude, directionX, directionZ, frequency, speed, phase] = WATER_VISUAL_CONFIG.waves[index]!;
    const angle = (x * directionX + z * directionZ) * frequency - time * speed + phase;
    height += amplitude * Math.sin(angle);
    const derivative = amplitude * frequency * Math.cos(angle);
    dx += derivative * directionX;
    dz += derivative * directionZ;
  }
  return { height, dx, dz };
}

export type WaterVisualLocomotion = number | string;

/** Fixed rings and fixed actor slots. No object or GPU buffer is allocated for an impulse. */
export class WaterInteractionEffects {
  readonly rippleData = new Float32Array(WATER_VISUAL_CONFIG.maximumRipples * 4);
  readonly splashData = new Float32Array(WATER_VISUAL_CONFIG.maximumSplashes * 4);
  readonly splashStrength = new Float32Array(WATER_VISUAL_CONFIG.maximumSplashes);
  readonly packedRipples = new Float32Array(WATER_VISUAL_CONFIG.maximumRipples * 4);
  private readonly ids: (string | undefined)[] = new Array(WATER_VISUAL_CONFIG.maximumActors);
  // x, z, lastSeen, lastImpulse; actor movement modes use a separate fixed array.
  private readonly actors = new Float64Array(WATER_VISUAL_CONFIG.maximumActors * 4);
  private readonly modes = new Uint8Array(WATER_VISUAL_CONFIG.maximumActors);
  private rippleCursor = 0;
  private splashCursor = 0;
  rippleCount = 0;
  splashCount = 0;

  constructor() { this.clear(); }

  clear(): void {
    this.rippleData.fill(0);
    this.splashData.fill(0);
    this.splashStrength.fill(0);
    this.packedRipples.fill(0);
    this.ids.fill(undefined);
    this.modes.fill(0);
    this.rippleCursor = 0;
    this.splashCursor = 0;
    this.rippleCount = 0;
    this.splashCount = 0;
    for (let index = 0; index < WATER_VISUAL_CONFIG.maximumSplashes; index += 1) this.splashData[index * 4 + 3] = -1e6;
  }

  updateActor(id: string, x: number, z: number, surfaceY: number, vx: number, vz: number, locomotion: WaterVisualLocomotion, time: number): void {
    if (!Number.isFinite(x + z + surfaceY + vx + vz + time)) return;
    const mode = typeof locomotion === "number" ? locomotion === 3 ? 2 : locomotion === 1 || locomotion === 2 ? 1 : 0
      : locomotion === "SWIM" ? 2 : locomotion.includes("WADE") ? 1 : 0;
    let slot = this.ids.indexOf(id);
    if (slot < 0) {
      slot = this.ids.indexOf(undefined);
      if (slot < 0) return;
      this.ids[slot] = id;
      this.modes[slot] = mode;
      this.actors[slot * 4 + 2] = -1e6;
    }
    const offset = slot * 4;
    const age = time - this.actors[offset + 2]!;
    const displacement = Math.hypot(x - this.actors[offset]!, z - this.actors[offset + 1]!);
    const continuous = age >= 0 && age < 1 && displacement < 8;
    const previous = this.modes[slot]!;
    if (continuous && mode !== previous && (mode === 2 || previous === 2 || (mode === 0 && previous === 1))) {
      this.addRipple(x, z, time, mode === 2 ? 0.85 : 0.55);
      this.addSplash(x, surfaceY, z, time, mode === 2 ? 1 : 0.65);
      this.actors[offset + 3] = time;
    }
    const speed = Math.hypot(vx, vz);
    const cadence = mode === 2 ? 0.30 : 0.38;
    if (continuous && mode > 0 && speed > 0.16 && displacement > 0.004 && time - this.actors[offset + 3]! >= cadence) {
      const back = mode === 2 ? 0.6 / Math.max(0.16, speed) : 0;
      this.addRipple(x - vx * back, z - vz * back, time, mode === 2 ? Math.min(0.62, 0.2 + speed * 0.11) : Math.min(0.35, 0.14 + speed * 0.04));
      this.actors[offset + 3] = time;
    }
    if (!continuous) this.actors[offset + 3] = time;
    this.actors[offset] = x;
    this.actors[offset + 1] = z;
    this.actors[offset + 2] = time;
    this.modes[slot] = mode;
  }

  addRipple(x: number, z: number, time: number, strength: number): void {
    if (!Number.isFinite(x + z + time + strength)) return;
    const offset = this.rippleCursor * 4;
    this.rippleData[offset] = x;
    this.rippleData[offset + 1] = z;
    this.rippleData[offset + 2] = time;
    this.rippleData[offset + 3] = Math.max(0, Math.min(1, strength));
    this.rippleCursor = (this.rippleCursor + 1) % WATER_VISUAL_CONFIG.maximumRipples;
  }

  addSplash(x: number, y: number, z: number, time: number, strength: number): void {
    if (!Number.isFinite(x + y + z + time + strength)) return;
    const offset = this.splashCursor * 4;
    this.splashData[offset] = x;
    this.splashData[offset + 1] = y;
    this.splashData[offset + 2] = z;
    this.splashData[offset + 3] = time;
    this.splashStrength[this.splashCursor] = Math.max(0, Math.min(1, strength));
    this.splashCursor = (this.splashCursor + 1) % WATER_VISUAL_CONFIG.maximumSplashes;
  }

  finish(time: number, focusX: number, focusZ: number, quality: AtmosphereQuality): void {
    const maximum = WATER_VISUAL_CONFIG.quality[quality].ripples;
    const radiusSquared = WATER_VISUAL_CONFIG.nearbyRadius ** 2;
    this.rippleCount = 0;
    this.splashCount = 0;
    // Match the float uniform clock so a just-emitted Float32 timestamp is not
    // interpreted as a few nanoseconds in the future by this CPU filter.
    const shaderTime = Math.fround(time);
    // Newest first makes lower quality retain the immediately visible response.
    for (let cursor = 0; cursor < WATER_VISUAL_CONFIG.maximumRipples && this.rippleCount < maximum; cursor += 1) {
      const index = (this.rippleCursor - 1 - cursor + WATER_VISUAL_CONFIG.maximumRipples) % WATER_VISUAL_CONFIG.maximumRipples;
      const offset = index * 4;
      const age = shaderTime - this.rippleData[offset + 2]!;
      const distanceSquared = (focusX - this.rippleData[offset]!) ** 2 + (focusZ - this.rippleData[offset + 1]!) ** 2;
      if (age < 0 || age > WATER_VISUAL_CONFIG.rippleLifetime || this.rippleData[offset + 3]! <= 0 || distanceSquared > radiusSquared) continue;
      for (let component = 0; component < 4; component += 1) this.packedRipples[this.rippleCount * 4 + component] = this.rippleData[offset + component]!;
      this.rippleCount += 1;
    }
    for (let index = 0; index < WATER_VISUAL_CONFIG.maximumSplashes; index += 1) {
      const offset = index * 4;
      const age = shaderTime - this.splashData[offset + 3]!;
      const distanceSquared = (focusX - this.splashData[offset]!) ** 2 + (focusZ - this.splashData[offset + 2]!) ** 2;
      if (age >= 0 && age <= WATER_VISUAL_CONFIG.splashLifetime && distanceSquared <= radiusSquared && this.splashStrength[index]! > 0) this.splashCount += 1;
      else this.splashStrength[index] = 0;
    }
    for (let index = 0; index < this.ids.length; index += 1) {
      if (time - this.actors[index * 4 + 2]! > 1) this.ids[index] = undefined;
    }
  }
}
